import { useCallback, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useAnchor } from "../ui/use-anchor";
import { useDismiss } from "../ui/use-dismiss";
import menus from "../ui/popovers.module.css";

/**
 * A button that opens a small panel of its own — the day's week list (ST9)
 * and the week's **⋯** (publication and changes).
 *
 * `RowMenu` holds buttons only; these two also say something before their
 * buttons (which week, whether it is published), so the panel takes any
 * content. Placement and dismissal are `RowMenu`'s, so the three behave alike:
 * drawn at the top of the page where no card can clip it, closed by a press
 * outside or Escape, and closed before a chosen action runs.
 */
export function Popover({
  label,
  buttonClassName,
  button,
  align,
  panelClassName,
  testId,
  panelTestId,
  children,
}: {
  /** For a screen reader, when the button's own content does not say it. */
  label?: string;
  buttonClassName: string | undefined;
  button: React.ReactNode;
  align: "start" | "end";
  panelClassName?: string;
  testId?: string;
  panelTestId?: string;
  /** The panel, told how to close itself. */
  children: (close: () => void) => React.ReactNode;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const close = useCallback((how: "outside" | "escape") => {
    setOpen(false);
    if (how === "escape") trigger.current?.focus();
  }, []);
  useDismiss(open, close, wrap, panel);
  const place = useAnchor(open, trigger, align);

  return (
    <span className={menus.menuWrap} ref={wrap}>
      <button
        ref={trigger}
        type="button"
        className={buttonClassName}
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((was) => !was)}
        data-testid={testId}
      >
        {button}
      </button>
      {open &&
        createPortal(
          <div
            id={panelId}
            ref={panel}
            className={`${menus.menu ?? ""} ${panelClassName ?? ""}`}
            style={place}
            data-testid={panelTestId}
          >
            {children(() => setOpen(false))}
          </div>,
          document.body,
        )}
    </span>
  );
}
